// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import xGoogleAnalyticsUpdate from "@conklin/App/GoogleAnalyticsConnector/Handlers/GA4";
import { getGoogleAnalyticsConnectorMapState } from "@conklin/App/GoogleAnalyticsConnector/Store/Selectors/GoogleAnalyticsSelectors";
import { getDataLayer } from "@conklin/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css } from "styled-components";

const enum fields {
    trackGoogleAnalyticsEvents = "trackGoogleAnalyticsEvents",
    checkoutFlowEvents = "checkoutFlowEvents",
    addToCartEvent = "addToCartEvent",
    addToCartEventName = "addToCartEventName",
    purchaseEvent = "purchaseEvent",
    purchaseEventName = "purchaseEventName",
    removeFromCartLineEvent = "removeFromCartLineEvent",
    removeFromCartLineEventName = "removeFromCartLineEventName",
    pdpViewEvent = "pdpViewEvent",
    pdpViewEventName = "pdpViewEventName",
    productImpressionEvent = "productImpressionEvent",
    productImpressionEventName = "productImpressionEventName",
    selectItemEvent = "selectItemEvent",
    selectItemEventName = "selectItemEventName",
    selectPromotionEvent = "selectPromotionEvent",
    selectPromotionEventName = "selectPromotionEventName",
    searchEvent = "searchEvent",
    searchEventName = "searchEventName",
    noStockSearchEvent = "noStockSearchEvent",
    noStockSearchEventName = "noStockSearchEventName",
    viewCartEvent = "viewCartEvent",
    viewCartEventName = "viewCartEventName",
    includeProductBrand = "includeProductBrand",
    includeProductCategory = "includeProductCategory",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.trackGoogleAnalyticsEvents]: boolean;
        [fields.checkoutFlowEvents]: boolean;
        [fields.addToCartEvent]: boolean;
        [fields.addToCartEventName]: string;
        [fields.purchaseEvent]: boolean;
        [fields.purchaseEventName]: string;
        [fields.removeFromCartLineEvent]: boolean;
        [fields.removeFromCartLineEventName]: string;
        [fields.pdpViewEvent]: boolean;
        [fields.pdpViewEventName]: string;
        [fields.productImpressionEvent]: boolean;
        [fields.productImpressionEventName]: string;
        [fields.selectItemEvent]: boolean;
        [fields.selectItemEventName]: string;
        [fields.selectPromotionEvent]: boolean;
        [fields.selectPromotionEventName]: string;
        [fields.searchEvent]: boolean;
        [fields.searchEventName]: string;
        [fields.noStockSearchEvent]: boolean;
        [fields.noStockSearchEventName]: string;
        [fields.viewCartEvent]: boolean;
        [fields.viewCartEventName]: string;
        [fields.includeProductBrand]: boolean;
        [fields.includeProductCategory]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        ...getGoogleAnalyticsConnectorMapState(state),
    };
};

const mapDispatchToProps = {
    xGoogleAnalyticsUpdate,
    resetShouldTrackPayment: () => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xGoogleAnalytics/setShouldTrackPayment",
            shouldTrackPayment: false,
        } as any);
    },
    resetShouldTrackBeginCheckout: () => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xGoogleAnalytics/setShouldTrackBeginOnePageCheckout",
            shouldTrackBeginOnePageCheckout: false,
        } as any);
    },
    resetShouldTrackNoStockSearch: () => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xGoogleAnalytics/setShouldTrackNoStockSearch",
            shouldTrackNoStockSearch: false,
        } as any);
    },
    setAfilliation: (affiliation?: string) => (dispatch: Dispatch<AnyAction>) => {
        dispatch({
            type: "xGoogleAnalytics/setAffiliation",
            affiliation,
        } as any);
    },
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface GoogleAnalyticsConnectorStyles {
    container?: InjectableCss;
}

const googleAnalyticsConnectorStyles: GoogleAnalyticsConnectorStyles = {
    container: {
        css: css`
            background: #e5e5e5;
            padding: 10px;
        `,
    },
};

const styles = googleAnalyticsConnectorStyles;
const GoogleAnalyticsConnector: FC<Props> = ({
    xGoogleAnalyticsUpdate,
    fields,
    cart,
    isCartPage,
    xGoogleAnalytics,
    currentPage,
    currencyCode,
    affiliation,
    locationId,
    isPLP,
    isPDP,
    PLPProducts,
    PDPProduct,
    resetShouldTrackPayment,
    resetShouldTrackBeginCheckout,
    resetShouldTrackNoStockSearch,
    userEmail,
    query,
    setAfilliation,
}) => {
    const { isInShell } = useShellContext();

    const [PDPInfoLoaded, setPDPInfoLoaded] = useState<boolean>(false);
    const [PLPInfoLoaded, setPLPInfoLoaded] = useState<boolean>(false);
    
    // Placeholder in CMS view
    if (isInShell) {
        return (
            <StyledWrapper {...styles.container} data-test-selectore="GoogleAnalyticsConnector">
                <Typography>{translate("Google Analytics Connector Widget")}</Typography>
            </StyledWrapper>
        );
    }

    useEffect(() => {
        if (!fields.trackGoogleAnalyticsEvents) {
            return;
        }

        xGoogleAnalyticsUpdate({
            trackGoogleAnalytics: true,
            checkoutFlowEvents: fields["checkoutFlowEvents"] || false,
            addToCartEvent: fields["addToCartEvent"] || false,
            addToCartEventName: fields["addToCartEventName"] || "add_to_cart",
            purchaseEvent: fields["purchaseEvent"] || false,
            purchaseEventName: fields["purchaseEventName"] || "purchase",
            removeFromCartLineEvent: fields["removeFromCartLineEvent"] || false,
            removeFromCartLineEventName: fields["removeFromCartLineEventName"] || "remove_from_cart",
            pdpViewEvent: fields["pdpViewEvent"] || false,
            pdpViewEventName: fields["pdpViewEventName"] || "view_item",
            productImpressionEvent: fields["productImpressionEvent"] || false,
            productImpressionEventName: fields["productImpressionEventName"] || "view_item_list",
            selectItemEvent: fields["selectItemEvent"] || false,
            selectItemEventName: fields["selectItemEventName"] || "select_item",
            selectPromotionEvent: fields["selectPromotionEvent"] || false,
            selectPromotionEventName: fields["selectPromotionEventName"] || "selectPromotionEventName",
            searchEvent: fields["searchEvent"] || false,
            searchEventName: fields["searchEventName"] || "search",
            noStockSearchEvent: fields["noStockSearchEvent"] || false,
            noStockSearchEventName: fields["noStockSearchEventName"] || "nostock_search",
            viewCartEvent: fields["viewCartEvent"] || false,
            viewCartEventName: fields["viewCartEventName"] || "view_cart",
            includeProductBrand: fields["includeProductBrand"] || false,
            includeProductCategory: fields["includeProductCategory"] || false,
        });
    }, [fields]);

    useEffect(() => {
        const dataLayer = getDataLayer();

        if (!cart) {
            return;
        }

        if (isCartPage && xGoogleAnalytics?.viewCartEvent) {
            setPDPInfoLoaded(false);
            setPLPInfoLoaded(false);

            const googleAnalyticsData = {
                event: xGoogleAnalytics?.viewCartEventName,
                ecommerce: {
                    currency: currencyCode,
                    value: cart.orderSubTotal,
                    items: cart?.cartLines?.map((cartItem, idx) => ({
                        item_id: cartItem.erpNumber,
                        item_name: cartItem.productName,
                        affiliation,
                        discount: cartItem.pricing ? cartItem.pricing.unitNetPrice < cartItem.pricing.unitListPrice ? cartItem.pricing.unitListPrice - cartItem.pricing.unitNetPrice : 0 : 0,
                        price: cartItem.pricing?.unitNetPrice,
                        quantity: cartItem.qtyOrdered,
                        index: idx,
                        item_brand: xGoogleAnalytics["includeProductBrand"] ? cartItem.brand?.name || "" : undefined,
                        item_category: xGoogleAnalytics["includeProductCategory"]
                            ? cartItem.properties?.["category"] || ""
                            : undefined,
                        location_id: locationId,
                    })),
                },
            };

            dataLayer.push(googleAnalyticsData);
        }
    }, [currentPage, cart?.id, xGoogleAnalytics]);

    useEffect(() => {
        const dataLayer = getDataLayer();

        if (dataLayer) {
            if (isPLP && xGoogleAnalytics?.productImpressionEvent && PLPProducts?.length && !PLPInfoLoaded) {
                setPLPInfoLoaded(true);
                setPDPInfoLoaded(false);
                const googleAnalyticsData = {
                    event: xGoogleAnalytics?.productImpressionEventName,
                    ecommerce: {
                        affiliation,
                        items: PLPProducts.map((product, idx) => ({
                            item_name: product?.productTitle,
                            item_id: product?.productNumber,
                            price: product?.pricing?.unitNetPrice,
                            index: idx,
                            item_brand: xGoogleAnalytics["includeProductBrand"] ? product?.brand?.name || "" : undefined,
                            item_category: xGoogleAnalytics["includeProductCategory"]
                                ? product?.properties?.["category"] || ""
                                : undefined,
                            item_list_id: xGoogleAnalytics["includeProductCategory"]
                                ? product?.properties?.["categoryId"] || ""
                                : undefined,
                        })),
                    },
                };

                dataLayer.push(googleAnalyticsData);
            }

            if (isPDP && xGoogleAnalytics?.pdpViewEvent && PDPProduct && !PDPInfoLoaded && PLPInfoLoaded) {
                setPDPInfoLoaded(true);
                setPLPInfoLoaded(false);
                const googleAnalyticsData = {
                    event: xGoogleAnalytics.pdpViewEventName,
                    ecommerce: {
                        currency: currencyCode,
                        value: cart?.orderGrandTotal,
                        affiliation,
                        items: [
                            {
                                item_name: PDPProduct?.productTitle,
                                item_id: PDPProduct?.productNumber,
                                price: PDPProduct.pricing?.unitNetPrice,
                                discount: PDPProduct.pricing ? PDPProduct.pricing.unitNetPrice < PDPProduct.pricing.unitListPrice ? PDPProduct.pricing.unitListPrice - PDPProduct.pricing.unitNetPrice : 0 : 0,
                                index: 0,
                                item_list_name: "Product Detail Page",
                                item_brand: xGoogleAnalytics["includeProductBrand"]
                                    ? PDPProduct.brand?.name || ""
                                    : undefined,
                                item_category: xGoogleAnalytics["includeProductCategory"]
                                    ? PDPProduct.properties?.["category"] || ""
                                    : undefined,
                                item_list_id: xGoogleAnalytics["includeProductCategory"]
                                    ? PDPProduct.properties?.["categoryId"] || ""
                                    : undefined,
                                affiliation,
                                qty: PDPProduct.minimumOrderQty || 1,
                            },
                        ],
                    },
                };

                dataLayer.push(googleAnalyticsData);
                const item = hasProductInViewItemList(PDPProduct.productNumber);
                if(item && xGoogleAnalytics.selectItemEvent) {
                    const googleAnalyticsData = {
                        event: xGoogleAnalytics.selectItemEventName,
                        ecommerce: {
                            item_list_id: xGoogleAnalytics["includeProductCategory"]
                                ? PDPProduct.properties?.["categoryId"] || ""
                                : undefined,
                            item_list_name: "Product Details Page",
                            affiliation,
                            items: [
                                {
                                    item_name: PDPProduct?.productTitle,
                                    item_id: PDPProduct?.productNumber,
                                    price: PDPProduct.pricing?.unitNetPrice,
                                    discount: PDPProduct.pricing ? PDPProduct.pricing.unitNetPrice < PDPProduct.pricing.unitListPrice ? PDPProduct.pricing.unitListPrice - PDPProduct.pricing.unitNetPrice : 0 : 0,
                                    index: 0,
                                    item_brand: xGoogleAnalytics["includeProductBrand"]
                                        ? PDPProduct.brand?.name || ""
                                        : undefined,
                                    item_category: xGoogleAnalytics["includeProductCategory"]
                                        ? PDPProduct.properties?.["category"] || ""
                                        : undefined,
                                    item_list_id: item.item_list_id,
                                    item_list_name: item.item_list_name,
                                    affiliation,
                                    qty: PDPProduct.minimumOrderQty || 1,
                                },
                            ],
                        },
                    }

                    dataLayer.push(googleAnalyticsData);
                }
            }
        }
    }, [PLPProducts, PDPProduct, currentPage, xGoogleAnalytics]);

    useEffect(() => {
        const dataLayer = getDataLayer();
        
        if (dataLayer && xGoogleAnalytics?.shouldTrackPayment) {
            (window as any)._GaCheckoutFunnel(
                3,
                cart,
                currencyCode,
                affiliation,
                xGoogleAnalytics.includeProductBrand,
                xGoogleAnalytics.includeProductCategory,
            );
            resetShouldTrackPayment();
        }
    }, [xGoogleAnalytics?.shouldTrackPayment]);

    useEffect(() => {
        const dataLayer = getDataLayer();
        if (dataLayer && xGoogleAnalytics?.checkoutFlowEvents && cart && xGoogleAnalytics?.shouldTrackBeginOnePageCheckout) {
            (window as any)._GaCheckoutFunnel(
                1,
                cart,
                currencyCode,
                affiliation,
                xGoogleAnalytics.includeProductBrand,
                xGoogleAnalytics.includeProductCategory,
            );
            resetShouldTrackBeginCheckout();
        }
    }, [xGoogleAnalytics?.shouldTrackBeginOnePageCheckout, cart]);

    useEffect(() => {
        setAfilliation(affiliation);
    }, [affiliation]);

    useEffect(() => {
        const dataLayer = getDataLayer();
        if (dataLayer && xGoogleAnalytics?.shouldTrackNoStockSearch) {
            const nonStock = PLPProducts?.every(p => p.invntory?.qtyOnHand === 0);
            if(nonStock) {
                const googleAnalyticsData = {
                    event: xGoogleAnalytics.noStockSearchEventName,
                    search_term: query,
                    numResults: PLPProducts?.length,
                    affiliation,
                    user: userEmail,
                    items: PLPProducts?.map((product, idx) => ({
                        item_name: product?.productTitle,
                        item_id: product?.productNumber,
                        price: product?.pricing?.unitNetPrice,
                        index: idx,
                        item_brand: xGoogleAnalytics["includeProductBrand"] ? product?.brand?.name || "" : undefined,
                        item_category: xGoogleAnalytics["includeProductCategory"]
                            ? product?.properties?.["category"] || ""
                            : undefined,
                        item_list_id: xGoogleAnalytics["includeProductCategory"]
                            ? product?.properties?.["categoryId"] || ""
                            : undefined,
                    })),
                };

                dataLayer.push(googleAnalyticsData);
            }
            resetShouldTrackNoStockSearch();
        }
    }, [xGoogleAnalytics?.shouldTrackNoStockSearch]);

    // Function to check for view_item_list events with a specific productNumber
    const hasProductInViewItemList = (productNumber: string) => {
        const dataLayer = getDataLayer();
        if(dataLayer) {
            for (const event of dataLayer) {
                if (event.event === xGoogleAnalytics?.productImpressionEventName && event.ecommerce?.items) {
                    for (const item of event.ecommerce.items) {
                        if (item.item_id === productNumber) {
                            return item;
                        }
                    }
                }
            }
        }
        return false;
    }

    return null;
};

const basicEventsTab = {
    displayName: "Basic Events",
    sortOrder: 1,
};
const customEventsTab = {
    displayName: "Custom Events",
    sortOrder: 2,
};
const settingsTab = {
    displayName: "Settings",
    sortOrder: 3,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(GoogleAnalyticsConnector),
    definition: {
        displayName: "Google Analytics Connector",
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.trackGoogleAnalyticsEvents,
                displayName: "Track GoogleAnalytics Events",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
            },
            {
                name: fields.addToCartEvent,
                displayName: "Add To Cart (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.addToCartEventName,
                displayName: "Add To Cart (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "add_to_cart",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.addToCartEvent,
            },
            {
                name: fields.removeFromCartLineEvent,
                displayName: "Remove Cartline (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.removeFromCartLineEventName,
                displayName: "Remove Cartline (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "remove_from_cart",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.removeFromCartLineEvent,
            },
            {
                name: fields.pdpViewEvent,
                displayName: "PDP View (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.pdpViewEventName,
                displayName: "PDP View (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "view_item",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.pdpViewEvent,
            },
            {
                name: fields.productImpressionEvent,
                displayName: "Product Impression (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.productImpressionEventName,
                displayName: "Product Impression (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "view_item_list",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.productImpressionEvent,
            },
            {
                name: fields.selectItemEvent,
                displayName: "Select Item (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.selectItemEventName,
                displayName: "Select Item (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "select_item",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.selectItemEvent,
            },
            {
                name: fields.viewCartEvent,
                displayName: "View Cart (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.viewCartEventName,
                displayName: "View Cart (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "view_cart",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.viewCartEvent,
            },
            {
                name: fields.purchaseEvent,
                displayName: "Purchase (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.purchaseEventName,
                displayName: "Purchase (Event Name)",
                editorTemplate: "TextField",
                defaultValue: "purchase",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.purchaseEvent,
            },
            {
                name: fields.checkoutFlowEvents,
                displayName: "Checkout Flow (Event)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.selectPromotionEvent,
                displayName: "Select Promotion Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.selectPromotionEventName,
                displayName: "Select Promotion Event Name",
                editorTemplate: "TextField",
                defaultValue: "select-promotion",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.selectPromotionEvent,
            },
            {
                name: fields.searchEvent,
                displayName: "Search Event",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.searchEventName,
                displayName: "Search Event Name",
                editorTemplate: "TextField",
                defaultValue: "search",
                fieldType: "General",
                tab: basicEventsTab,
                isVisible: item => item.fields.searchEvent,
            },
            {
                name: fields.noStockSearchEvent,
                displayName: "No Stock Search Event",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.trackGoogleAnalyticsEvents,
            },
            {
                name: fields.noStockSearchEventName,
                displayName: "No Stock Search Event Name",
                editorTemplate: "TextField",
                defaultValue: "nostock_search",
                fieldType: "General",
                tab: customEventsTab,
                isVisible: item => item.fields.noStockSearchEvent,
            },
            {
                name: fields.includeProductBrand,
                displayName: "Include Product Brand (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.includeProductCategory,
                displayName: "Include Product Category (data)",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
        ],
    },
};

export default widgetModule;
